/**
 * Utilities for handling date formatting and timezone conversions consistently across the app
 */

import { formatDistanceToNow } from 'date-fns';

/**
 * Format a date for display, handling UTC date strings properly to prevent timezone shifts
 *
 * @param {string|Date} date - The date to format
 * @param {string} [fallback='Not set'] - Fallback string to display if date is null/undefined
 * @param {Object} [options] - Formatting options for toLocaleDateString
 * @returns {string} Formatted date string
 */
export const formatDate = (date, fallback = 'Not set', options = null) => {
    if (!date) return fallback;

    // Default formatting options
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    };

    const formattingOptions = options || defaultOptions;

    // Fix timezone handling for dates without time component
    // If the date is in format YYYY-MM-DD, ensure it's treated as UTC date
    // This prevents the date from shifting to previous day in some timezones
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Append 'T12:00:00Z' to ensure it's noon UTC, avoiding any date shift
        const utcDate = new Date(`${date}T12:00:00Z`);
        return utcDate.toLocaleDateString('en-US', formattingOptions);
    }

    // For datetime strings, append 'Z' if no timezone info to ensure UTC parsing
    if (typeof date === 'string' && date.includes('T')) {
        if (!date.endsWith('Z') && !date.includes('+') && !date.includes('-', 10)) {
            date = date + 'Z';
        }
    }

    return new Date(date).toLocaleDateString('en-US', formattingOptions);
};

/**
 * Format a notification timestamp for display
 * Backend sends UTC timestamps
 *
 * @param {string|Date} timestamp - UTC timestamp from backend
 * @returns {string} Formatted "time ago" string (e.g., "2 hours ago")
 */
export const formatNotificationTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';

    try {
        // Add 'Z' suffix if not present to ensure UTC parsing
        let dateString = timestamp.toString();
        if (
            !dateString.endsWith('Z') &&
            !dateString.includes('+') &&
            !dateString.includes('-', 10)
        ) {
            dateString = dateString + 'Z';
        }

        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return 'Invalid date';
        }

        return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
        return 'Unknown time';
    }
};

/**
 * Format a date with time for display, handling UTC date-time strings properly
 *
 * @param {string|Date} date - The date to format
 * @param {string} [fallback='Not set'] - Fallback string to display if date is null/undefined
 * @returns {string} Formatted date-time string
 */
export const formatDateTime = (date, fallback = 'Not set') => {
    if (!date) return fallback;

    let dateString = date.toString();

    // Add 'Z' suffix if not present (assuming UTC from backend)
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
        dateString = dateString + 'Z';
    }

    const localDate = new Date(dateString);

    return localDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    });
};

/**
 * Get today's date in YYYY-MM-DD format
 *
 * @returns {string} Today's date in YYYY-MM-DD format
 */
export const getTodayFormatted = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Calculate days ago from a date string, comparing dates only (ignoring time).
 * Handles UTC date strings properly to prevent timezone issues.
 *
 * @param {string|Date} dateStr - The date to compare
 * @returns {number|null} Number of days ago (0 = today), or null if no date
 */
export const daysAgo = (dateStr) => {
    if (!dateStr) return null;

    // Parse the input date - treat date-only strings as local dates
    let inputDate;
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Date-only string (YYYY-MM-DD) - parse as local date
        const [year, month, day] = dateStr.split('-').map(Number);
        inputDate = new Date(year, month - 1, day);
    } else {
        inputDate = new Date(dateStr);
    }

    // Get today's date at midnight local time
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get input date at midnight local time
    inputDate.setHours(0, 0, 0, 0);

    // Calculate difference in days
    const diffMs = today - inputDate;
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * Format a date as "X days ago" string
 *
 * @param {string|Date} dateStr - The date to format
 * @returns {string} Formatted string like "Today", "1d ago", "5d ago", or "Never"
 */
export const formatDaysAgo = (dateStr) => {
    const days = daysAgo(dateStr);
    if (days === null) return 'Never';
    if (days <= 0) return 'Today';
    if (days === 1) return '1d ago';
    return `${days}d ago`;
};
